import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Loader2, Image as ImageIcon, Film } from "lucide-react";
import api, { API, formatApiErrorDetail } from "@/lib/api";

/**
 * File uploader. Uploads to /api/uploads/photo or /api/uploads/video, returns file_id
 * and stores as { file_id, url } in the parent's state.
 *
 * Props:
 *  - kind: "photo" | "video"
 *  - value: array of { file_id, url } or single object (when single=true)
 *  - onChange: (nextValue) => void
 *  - max: maximum number of files (default 8 for photo, 1 for video)
 *  - single: if true, only allow one file
 */
export default function FileUpload({
  kind = "photo",
  value,
  onChange,
  max,
  single = false,
  label,
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const isPhoto = kind === "photo";
  const accept = isPhoto ? "image/*" : "video/*";
  const limit = max ?? (isPhoto ? 8 : 1);
  const items = single ? (value ? [value] : []) : value || [];

  const upload = async (files) => {
    setBusy(true);
    const uploaded = [];
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post(`/uploads/${kind}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push({ file_id: data.file_id, url: `${API}${data.url.replace(/^\/api/, "")}` });
      }
      if (single) {
        onChange(uploaded[0] || null);
      } else {
        onChange([...(items || []), ...uploaded].slice(0, limit));
      }
      toast.success(`${uploaded.length} ${kind}${uploaded.length > 1 ? "s" : ""} uploaded`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = single ? 1 : limit - items.length;
    if (files.length > remaining) toast.info(`Only ${remaining} more allowed`);
    upload(files.slice(0, remaining));
  };

  const remove = (fid) => {
    if (single) onChange(null);
    else onChange(items.filter((i) => i.file_id !== fid));
  };

  return (
    <div data-testid={`upload-${kind}`}>
      {label && (
        <label className="mb-2 block text-[10px] uppercase tracking-luxe text-white/40">{label}</label>
      )}
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
        {items.map((it) => (
          <div key={it.file_id} className="group relative aspect-[3/4] overflow-hidden border border-white/10 bg-white/5">
            {isPhoto ? (
              <img src={it.url} alt="" className="h-full w-full object-cover" />
            ) : (
              <video src={it.url} className="h-full w-full object-cover" muted />
            )}
            <button
              type="button"
              onClick={() => remove(it.file_id)}
              data-testid={`remove-${kind}-${it.file_id}`}
              className="absolute right-1 top-1 rounded-full bg-black/70 p-1 text-white/70 opacity-0 transition-opacity group-hover:opacity-100 hover:text-velvet-rose"
            >
              <X size={13} />
            </button>
          </div>
        ))}
        {items.length < limit && (
          <button
            type="button"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            data-testid={`upload-${kind}-btn`}
            className="flex aspect-[3/4] flex-col items-center justify-center gap-2 border border-dashed border-white/20 bg-white/[0.02] text-white/50 transition-colors hover:border-velvet-gold hover:text-velvet-gold disabled:opacity-50"
          >
            {busy ? (
              <Loader2 className="animate-spin" size={20} />
            ) : (
              <>
                {isPhoto ? <ImageIcon size={20} /> : <Film size={20} />}
                <span className="text-[10px] uppercase tracking-luxe">
                  <Upload size={11} className="inline" /> {isPhoto ? "Photo" : "Video"}
                </span>
              </>
            )}
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={!single}
        onChange={handleFiles}
        className="hidden"
      />
    </div>
  );
}
